/**
 * Tenant-aware identity API. Exposes the per-org sign-in config — Cognito
 * pool/client IDs etc. — that the frontend uses to bootstrap Amplify, plus a
 * `whoAmI()` probe for the principal behind the current token.
 *
 * The sign-in config is served by V2 identity
 * (`GET /identity/v1/tenants/{orgId}/sign-in-config`) when `identity` is
 * switched on in `VITE_API_V2_SERVICES`, and by the legacy IAM service
 * (`GET /iam-svc/tenant/{orgId}/sign-in-config`) otherwise. Both answers carry
 * the same key set, so the normalised result — and therefore the Amplify
 * config built from it — is identical either way.
 */
import axios from 'axios';
import { attachActivityInterceptors } from '@/api/requestActivity';
import { baseUrlForPath, isV2Service, readApiHostEnv, type ApiHostEnv } from '@/api/apiHosts';
import type { components } from '@/api/openapi.types.identity';

/** The principal the V2 authorizer resolved for the current token. */
export type Principal = components['schemas']['Principal'];

export interface TenantSignInConfig {
  orgId: string;
  region: string;
  userPoolId: string;
  userPoolClientId: string;
  identityPoolId?: string;
  /** Optional MFA hints, etc. Legacy only — the V2 answer carries no `Features` key. */
  features?: {
    mfaEnabled?: boolean;
    forceMfa?: boolean;
  };
}

// Wire shape of the sign-in config, from either host. The backend uses
// PascalCase Cognito-native names; the rest of the frontend stays in
// camelCase, so we normalize at this boundary. Discovered during Wave 3.1:
// prior to this mapping, `configureAmplify` got undefined
// userPoolId/userPoolClientId and silently failed to configure Amplify,
// making sign-in impossible via the v2 web app.
//
// V2 returns exactly the legacy key set minus `Features` (lead ruling §8 of
// `06-LEAD-RULINGS.md`: byte-identical wins), so `Features` stays optional
// here and simply never appears on a V2 answer.
interface TenantSignInConfigWire {
  Region: string;
  UserPoolId: string;
  AppClientId: string;
  CognitoIdentityPoolId?: string;
  Features?: {
    MfaEnabled?: boolean;
    ForceMfa?: boolean;
  };
}

/** Which front end is asking. V2 uses it to pick the app client. */
const V2_APP = 'hr-web';

/**
 * Bare axios instance — sign-in-config is unauthenticated by definition
 * (the user has no session yet). Don't use the shared httpClient here
 * because that adds the auth interceptor.
 *
 * `baseURL` stays the legacy host and is overridden per request by
 * `signInConfigRequest()`, so the same instance — same 15 s timeout, same
 * activity instrumentation — serves both hosts.
 */
const iamPublic = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 15_000,
});

// Surface the unauthenticated sign-in-config lookup on /welcome in the global
// top progress bar too, using the same shared instrumentation as httpClient.
attachActivityInterceptors(iamPublic);

/**
 * Where the sign-in config for an org is fetched from, and with what.
 * @param orgId The organisation code typed on the welcome screen.
 * @param env The host env; defaults to `import.meta.env`.
 * @returns The axios `baseURL`, `url` and `params` for the lookup.
 */
export function signInConfigRequest(
  orgId: string,
  env: ApiHostEnv = readApiHostEnv(),
): { baseURL: string; url: string; params?: Record<string, string> } {
  if (isV2Service('identity', env)) {
    const url = `/identity/v1/tenants/${encodeURIComponent(orgId)}/sign-in-config`;
    return { baseURL: baseUrlForPath(url, env), url, params: { app: V2_APP } };
  }
  return {
    baseURL: env.legacyUrl,
    url: `/iam-svc/tenant/${encodeURIComponent(orgId)}/sign-in-config`,
  };
}

/**
 * Normalise the PascalCase wire answer into the camelCase shape the app uses.
 *
 * The only normalisation point for this wire shape (CLAUDE.md). Optional keys
 * are omitted rather than set to `undefined`, so the object a V2 answer
 * produces is identical to the one the legacy answer produced.
 * @param orgId The organisation the config was fetched for.
 * @param data The raw wire answer.
 * @returns The normalised sign-in config.
 */
export function normalizeTenantSignInConfig(
  orgId: string,
  data: TenantSignInConfigWire,
): TenantSignInConfig {
  const features: TenantSignInConfig['features'] = {};
  if (data.Features?.MfaEnabled !== undefined) features.mfaEnabled = data.Features.MfaEnabled;
  if (data.Features?.ForceMfa !== undefined) features.forceMfa = data.Features.ForceMfa;
  return {
    orgId,
    region: data.Region,
    userPoolId: data.UserPoolId,
    userPoolClientId: data.AppClientId,
    ...(data.CognitoIdentityPoolId ? { identityPoolId: data.CognitoIdentityPoolId } : {}),
    ...(Object.keys(features).length > 0 ? { features } : {}),
  };
}

/**
 * Fetch one org's sign-in config, unauthenticated.
 * @param orgId The organisation code typed on the welcome screen.
 * @returns The normalised Cognito config to hand to `configureAmplify()`.
 * @throws When `orgId` is empty, or the lookup fails.
 */
export async function fetchTenantSignInConfig(orgId: string): Promise<TenantSignInConfig> {
  if (!orgId) throw new Error('orgId is required');
  const { baseURL, url, params } = signInConfigRequest(orgId);
  const { data } = await iamPublic.get<TenantSignInConfigWire>(url, {
    baseURL,
    ...(params ? { params } : {}),
  });
  return normalizeTenantSignInConfig(orgId, data);
}

/**
 * Ask the V2 platform who it thinks the caller is.
 *
 * One authenticated call on the shared `httpClient`, so it proves the whole
 * chain: the ID token Amplify holds, the bearer header, the V2 host routing
 * and the V2 authorizer. Used by the W1 E2E evidence and available to any
 * later caller that needs the resolved principal.
 * @returns The principal resolved from the current token.
 * @throws When there is no valid session (401) or the call fails.
 */
export async function whoAmI(): Promise<Principal> {
  // Imported lazily: this module is pulled in at boot by the org store, and a
  // static import would drag the authenticated client (and Amplify with it)
  // into that path for a call almost no boot makes.
  const { httpClient } = await import('@/api/httpClient');
  const { data } = await httpClient.get<Principal>('/identity/v1/me');
  return data;
}

/**
 * Dev-only test hook. The W1 E2E signs in for real and then needs to drive one
 * authenticated V2 call to prove the token is accepted by the V2 authorizer;
 * nothing in the UI calls `whoAmI()` yet, and adding a button for it would be
 * a UI change. Stripped from any production build by the `DEV` guard.
 */
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as unknown as { __ohrWhoAmI?: typeof whoAmI }).__ohrWhoAmI = whoAmI;
}
